import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { extractErrorMessage } from '../../utils/errorHandler';
import { formatDateTimeJST } from '../../utils/dateUtils';

// ==============================
// 型定義
// ==============================

interface AuditLog {
  id: number;
  table_name: string;
  operation: string;
  record_id: string;
  old_values: any;
  new_values: any;
  changed_by: string;
  changed_by_name?: string;
  changed_at: string;
}

interface OperationLog {
  id: number;
  employee_id: string;
  employee_name?: string;
  operation_type: string;
  request_method: string;
  request_path: string;
  request_body: any;
  response_status: number;
  success: boolean;
  ip_address: string;
  user_agent: string;
  request_id: string;
  duration_ms: number;
  created_at: string;
}

interface BusinessEventLog {
  id: number;
  event_type: string;
  event_category: string;
  employee_id: string;
  employee_name?: string;
  target_type?: string;
  target_id?: string;
  event_summary: string;
  event_details: any;
  severity: string;
  created_at: string;
}

interface LogStats {
  total_count: number;
  count_24h: number;
  count_7d?: number;
  count_30d?: number;
  error_count?: number;
  critical_count?: number;
}

interface Stats {
  audit_logs: LogStats;
  operation_logs: LogStats;
  business_event_logs: LogStats;
}

// ==============================
// メインコンポーネント
// ==============================

const AuditLogsPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // フィルタ
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [tableName, setTableName] = useState('');
  const [eventCategory, setEventCategory] = useState('');
  const [eventType, setEventType] = useState('');
  const [severity, setSeverity] = useState('');
  const [requestMethod, setRequestMethod] = useState('');
  const [successFilter, setSuccessFilter] = useState('');

  // ページネーション
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);

  // データ
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [operationLogs, setOperationLogs] = useState<OperationLog[]>([]);
  const [businessEventLogs, setBusinessEventLogs] = useState<BusinessEventLog[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);

  // ==============================
  // データ取得
  // ==============================

  useEffect(() => {
    fetchLogs();
  }, [tabValue, page, rowsPerPage]);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await api.get('/audit/stats');
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (err) {
      console.error('統計情報取得エラー:', err);
    }
  };

  const fetchLogs = async () => {
    setLoading(true);
    setError(null);

    try {
      let endpoint = '';
      const params: any = {
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      };

      // フィルタ条件を追加
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (employeeId) params.employee_id = employeeId;

      if (tabValue === 0) {
        // 監査ログ
        endpoint = '/audit/logs';
        if (tableName) params.table_name = tableName;
      } else if (tabValue === 1) {
        // 業務操作ログ
        endpoint = '/audit/operations';
        if (requestMethod) params.request_method = requestMethod;
        if (successFilter !== '') params.success = successFilter;
      } else if (tabValue === 2) {
        // ビジネスイベントログ
        endpoint = '/audit/events';
        if (eventCategory) params.event_category = eventCategory;
        if (eventType) params.event_type = eventType;
        if (severity) params.severity = severity;
      }

      const response = await api.get(endpoint, { params });

      if (response.data.success) {
        const { logs, total: totalCount } = response.data.data;
        setTotal(totalCount);

        if (tabValue === 0) {
          setAuditLogs(logs);
        } else if (tabValue === 1) {
          setOperationLogs(logs);
        } else if (tabValue === 2) {
          setBusinessEventLogs(logs);
        }
      }
    } catch (err) {
      console.error('ログ取得エラー:', err);
      setError(extractErrorMessage(err, 'ログの取得に失敗しました'));
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(0);
    fetchLogs();
  };

  const handleReset = () => {
    setStartDate('');
    setEndDate('');
    setEmployeeId('');
    setTableName('');
    setEventCategory('');
    setEventType('');
    setSeverity('');
    setRequestMethod('');
    setSuccessFilter('');
    setPage(0);
    fetchLogs();
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setPage(0);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // ==============================
  // レンダリング補助関数
  // ==============================

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'error';
      case 'error':
        return 'warning';
      case 'warning':
        return 'warning';
      case 'info':
        return 'info';
      case 'debug':
        return 'default';
      default:
        return 'default';
    }
  };

  const renderFilters = () => {
    return (
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="開始日"
              type="date"
              size="small"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="終了日"
              type="date"
              size="small"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="社員ID"
              size="small"
              value={employeeId}
              onChange={(e) => setEmployeeId(e.target.value)}
            />
          </Grid>

          {tabValue === 0 && (
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="テーブル名"
                size="small"
                value={tableName}
                onChange={(e) => setTableName(e.target.value)}
              />
            </Grid>
          )}

          {tabValue === 1 && (
            <>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>HTTPメソッド</InputLabel>
                  <Select
                    value={requestMethod}
                    onChange={(e) => setRequestMethod(e.target.value)}
                    label="HTTPメソッド"
                  >
                    <MenuItem value="">すべて</MenuItem>
                    <MenuItem value="GET">GET</MenuItem>
                    <MenuItem value="POST">POST</MenuItem>
                    <MenuItem value="PUT">PUT</MenuItem>
                    <MenuItem value="PATCH">PATCH</MenuItem>
                    <MenuItem value="DELETE">DELETE</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>成功/失敗</InputLabel>
                  <Select
                    value={successFilter}
                    onChange={(e) => setSuccessFilter(e.target.value)}
                    label="成功/失敗"
                  >
                    <MenuItem value="">すべて</MenuItem>
                    <MenuItem value="true">成功</MenuItem>
                    <MenuItem value="false">失敗</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </>
          )}

          {tabValue === 2 && (
            <>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>カテゴリ</InputLabel>
                  <Select
                    value={eventCategory}
                    onChange={(e) => setEventCategory(e.target.value)}
                    label="カテゴリ"
                  >
                    <MenuItem value="">すべて</MenuItem>
                    <MenuItem value="employee">社員</MenuItem>
                    <MenuItem value="department">部門</MenuItem>
                    <MenuItem value="evaluation">評価</MenuItem>
                    <MenuItem value="skill">スキル</MenuItem>
                    <MenuItem value="sales">営業</MenuItem>
                    <MenuItem value="system">システム</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>重要度</InputLabel>
                  <Select
                    value={severity}
                    onChange={(e) => setSeverity(e.target.value)}
                    label="重要度"
                  >
                    <MenuItem value="">すべて</MenuItem>
                    <MenuItem value="critical">Critical</MenuItem>
                    <MenuItem value="error">Error</MenuItem>
                    <MenuItem value="warning">Warning</MenuItem>
                    <MenuItem value="info">Info</MenuItem>
                    <MenuItem value="debug">Debug</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </>
          )}

          <Grid item xs={12} md={12}>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button variant="outlined" onClick={handleReset}>
                リセット
              </Button>
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleSearch}
              >
                検索
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    );
  };

  const renderAuditLogsTable = () => {
    return (
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>変更日時</TableCell>
              <TableCell>テーブル</TableCell>
              <TableCell>操作</TableCell>
              <TableCell>レコードID</TableCell>
              <TableCell>変更者</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {auditLogs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography color="text.secondary">ログがありません</Typography>
                </TableCell>
              </TableRow>
            ) : (
              auditLogs.map((log) => (
                <TableRow key={log.id} hover>
                  <TableCell>{formatDateTimeJST(log.changed_at, true)}</TableCell>
                  <TableCell>
                    <Chip label={log.table_name} size="small" />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={log.operation}
                      size="small"
                      color={
                        log.operation === 'INSERT'
                          ? 'success'
                          : log.operation === 'UPDATE'
                          ? 'primary'
                          : 'error'
                      }
                    />
                  </TableCell>
                  <TableCell>{log.record_id}</TableCell>
                  <TableCell>
                    {log.changed_by_name || log.changed_by}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderOperationLogsTable = () => {
    return (
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>実行日時</TableCell>
              <TableCell>社員</TableCell>
              <TableCell>メソッド</TableCell>
              <TableCell>パス</TableCell>
              <TableCell>ステータス</TableCell>
              <TableCell>処理時間</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {operationLogs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography color="text.secondary">ログがありません</Typography>
                </TableCell>
              </TableRow>
            ) : (
              operationLogs.map((log) => (
                <TableRow key={log.id} hover>
                  <TableCell>{formatDateTimeJST(log.created_at, true)}</TableCell>
                  <TableCell>
                    {log.employee_name || log.employee_id}
                  </TableCell>
                  <TableCell>
                    <Chip label={log.request_method} size="small" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                      {log.request_path}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={log.response_status}
                      size="small"
                      color={log.success ? 'success' : 'error'}
                    />
                  </TableCell>
                  <TableCell>{log.duration_ms}ms</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderBusinessEventLogsTable = () => {
    return (
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>発生日時</TableCell>
              <TableCell>カテゴリ</TableCell>
              <TableCell>イベント種別</TableCell>
              <TableCell>実行者</TableCell>
              <TableCell>概要</TableCell>
              <TableCell>重要度</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {businessEventLogs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography color="text.secondary">ログがありません</Typography>
                </TableCell>
              </TableRow>
            ) : (
              businessEventLogs.map((log) => (
                <TableRow key={log.id} hover>
                  <TableCell>{formatDateTimeJST(log.created_at, true)}</TableCell>
                  <TableCell>
                    <Chip label={log.event_category} size="small" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                      {log.event_type}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {log.employee_name || log.employee_id}
                  </TableCell>
                  <TableCell>{log.event_summary}</TableCell>
                  <TableCell>
                    <Chip
                      label={log.severity.toUpperCase()}
                      size="small"
                      color={getSeverityColor(log.severity) as any}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // ==============================
  // レンダリング
  // ==============================

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <AssessmentIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            監査ログ
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchLogs}>
          更新
        </Button>
      </Box>

      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                監査ログ
              </Typography>
              <Typography variant="h5">
                {stats.audit_logs.total_count.toLocaleString()}件
              </Typography>
              <Typography variant="caption" color="text.secondary">
                24時間: {stats.audit_logs.count_24h}件
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                業務操作ログ
              </Typography>
              <Typography variant="h5">
                {stats.operation_logs.total_count.toLocaleString()}件
              </Typography>
              <Typography variant="caption" color="text.secondary">
                24時間: {stats.operation_logs.count_24h}件 / エラー: {stats.operation_logs.error_count}件
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                ビジネスイベントログ
              </Typography>
              <Typography variant="h5">
                {stats.business_event_logs.total_count.toLocaleString()}件
              </Typography>
              <Typography variant="caption" color="text.secondary">
                24時間: {stats.business_event_logs.count_24h}件 / Critical: {stats.business_event_logs.critical_count}件
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2 }}>
        <Tab label="監査ログ (audit_logs)" />
        <Tab label="業務操作ログ (operation_logs)" />
        <Tab label="ビジネスイベントログ (business_event_logs)" />
      </Tabs>

      {renderFilters()}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {tabValue === 0 && renderAuditLogsTable()}
          {tabValue === 1 && renderOperationLogsTable()}
          {tabValue === 2 && renderBusinessEventLogsTable()}

          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="1ページあたりの行数"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} / ${count !== -1 ? count : `${to}以上`}`
            }
          />
        </>
      )}
    </Box>
  );
};

export default AuditLogsPage;
